import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '../../shared/components/Button';
import { Field, Input } from '../../shared/components/Field';
import { Alert } from '../../shared/components/feedback';
import { BrandLogo } from '../../shared/components/icons';
import { COMPANY, SiteFooter } from '../../shared/components/SiteFooter';

const SUPPORT_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL ?? COMPANY.email;

function createRequestCode() {
  const stamp = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `AT-DEL-${stamp}-${rand}`;
}

export default function DataDeletionPage() {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState<{
    code: string;
    email: string;
  } | null>(null);

  const mailtoHref = useMemo(() => {
    if (!submitted) return '#';
    const subject = encodeURIComponent(
      `[Auto Threads] 사용자 데이터 삭제 요청 (${submitted.code})`
    );
    const body = encodeURIComponent(
      [
        '아래 내용으로 데이터 삭제를 요청합니다.',
        '',
        `요청 코드: ${submitted.code}`,
        `계정 이메일: ${submitted.email}`,
        note.trim() ? `추가 요청: ${note.trim()}` : '',
        '',
        '가입에 사용한 이메일과 동일한 주소로 보내 주세요.',
      ]
        .filter(Boolean)
        .join('\n')
    );
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  }, [note, submitted]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setSubmitted({
      code: createRequestCode(),
      email: trimmed,
    });
  };

  return (
    <Shell>
      <Page>
        <Brand to="/">
          <BrandMark><BrandLogo size={30} /></BrandMark>
          <div>
            <BrandTitle>Auto Threads</BrandTitle>
            <BrandSubtitle>사용자 데이터 삭제</BrandSubtitle>
          </div>
        </Brand>

        <Doc>
        <DocHeader>
          <h1>사용자 데이터 삭제 안내</h1>
          <Meta>Meta / Threads 연동 앱 이용자를 위한 삭제 요청 안내</Meta>
          <Lead>
            Auto Threads는 Meta 플랫폼 정책을 준수하며, 이용자가 본인 계정과
            관련하여 서비스에 저장된 데이터의 삭제를 요청할 수 있는 방법을
            제공합니다. 아래 양식에서 요청 코드를 만든 뒤 이메일로 삭제를 요청할 수 있습니다.
          </Lead>
        </DocHeader>

        <Contents aria-label="문서 목차">
          <strong>빠르게 찾아보기</strong>
          <ol>
            <li><a href="#section-1">1. 삭제되는 데이터</a></li>
            <li><a href="#section-2">2. 앱/웹사이트에서 삭제 요청하는 방법</a></li>
            <li><a href="#section-3">3. 데이터 삭제 요청</a></li>
            <li><a href="#section-4">4. 처리 기간</a></li>
            <li><a href="#section-5">5. 문의</a></li>
          </ol>
        </Contents>

        <Section id="section-1">
          <h2>1. 삭제되는 데이터</h2>
          <p>삭제 요청이 처리되면 아래 정보가 제거되거나 파기됩니다.</p>
          <ul>
            <li>회원 계정 정보(이메일, 이름, 휴대폰, 주소 등)</li>
            <li>Threads 연동 계정 정보 및 관련 토큰</li>
            <li>생성·예약·발행 게시글, 주제, 말투/톤 분석 데이터</li>
            <li>알림 설정 및 서비스 이용과 연결된 개인 식별 데이터</li>
          </ul>
          <p>
            관련 법령상 일정 기간 보관이 필요한 기록은 해당 기간 동안만 보관된
            뒤 파기됩니다. Threads에 이미 게시된 글은 Meta/Threads 측에서
            별도로 관리되며, 필요 시 Threads 앱·웹에서 직접 삭제해 주세요.
          </p>
        </Section>

        <Section id="section-2">
          <h2>2. 앱/웹사이트에서 삭제 요청하는 방법</h2>
          <ol>
            <li>Auto Threads에 로그인합니다.</li>
            <li>
              <Link to="/settings">설정</Link> → <strong>법적 고지</strong> →
              <strong> 사용자 데이터 삭제</strong>로 이동합니다.
            </li>
            <li>아래 요청 양식에 가입 이메일을 입력하고 제출합니다.</li>
            <li>
              발급된 요청 코드를 포함해 안내에 따라 확인 메일을 보내거나,
              요청이 접수되었는지 확인해 주세요.
            </li>
          </ol>
          <p>
            로그인할 수 없는 경우에도 이 페이지의 양식으로 요청할 수 있습니다.
            반드시 <strong>가입에 사용한 이메일</strong>을 입력해 주세요.
          </p>
        </Section>

        <Section id="section-3">
          <h2>3. 데이터 삭제 요청</h2>
          {!submitted ? (
            <Form onSubmit={handleSubmit}>
              <Field label="가입 이메일" htmlFor="deletion-email">
                <Input
                  id="deletion-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Field>
              <Field label="추가 요청 사항 (선택)" htmlFor="deletion-note">
                <Input
                  id="deletion-note"
                  placeholder="예: Threads 연동 데이터까지 모두 삭제"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Field>
              <Button type="submit" $size="lg" $fullWidth>
                삭제 요청 코드 발급
              </Button>
              <FormHint>
                이 단계에서는 요청 코드만 생성됩니다. 다음 화면에서 이메일을 보내야 삭제 요청이 접수됩니다.
              </FormHint>
            </Form>
          ) : (
            <ResultBox>
              <Alert tone="success">
                요청 코드가 준비되었습니다. 이메일을 보내 접수를 완료해 주세요.
              </Alert>
              <ResultCode>{submitted.code}</ResultCode>
              <ResultText>
                계정 <strong>{submitted.email}</strong>에 대한 데이터 삭제
                요청입니다. 확인을 위해 아래 버튼을 눌러 메일을 보내 주시거나,
                연락처({COMPANY.phone}, {COMPANY.email})로 동일 내용을
                문의해 주세요.
              </ResultText>
              <MailButton href={mailtoHref}>이메일 앱에서 요청 보내기</MailButton>
              <FormHint>
                처리 완료까지 보통 30일 이내가 소요될 수 있습니다. 요청 상태를
                확인할 때는 위 코드를 알려 주세요.
              </FormHint>
              <GhostButton
                type="button"
                onClick={() => {
                  setSubmitted(null);
                  setEmail('');
                  setNote('');
                }}
              >
                다른 이메일로 다시 요청
              </GhostButton>
            </ResultBox>
          )}
        </Section>

        <Section id="section-4">
          <h2>4. 처리 기간</h2>
          <p>
            요청이 접수되고 본인 확인이 완료되면, 관련 법령과 Meta 정책을
            고려하여 <strong>최대 30일 이내</strong>에 삭제를 진행합니다.
            처리가 완료되면 가능하면 요청에 사용한 이메일로 안내합니다.
          </p>
        </Section>

        <Section id="section-5">
          <h2>5. 문의</h2>
          <ContactBox>
            <p>
              <strong>상호</strong> {COMPANY.name}
            </p>
            <p>
              <strong>대표</strong> {COMPANY.ceo}
            </p>
            <p>
              <strong>사업자등록번호</strong> {COMPANY.businessNumber}
            </p>
            <p>
              <strong>전화</strong>{' '}
              <a href={`tel:${COMPANY.phone.replace(/-/g, '')}`}>
                {COMPANY.phone}
              </a>
            </p>
            <p>
              <strong>이메일</strong>{' '}
              <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
            </p>
            <p>
              <strong>페이지</strong> /data-deletion
            </p>
            <p>
              <strong>관련</strong>{' '}
              <Link to="/privacy">개인정보 처리방침</Link>
            </p>
          </ContactBox>
        </Section>

        <DocFooter>
          <NavLinks>
            <Link to="/privacy">개인정보 처리방침</Link>
            <span aria-hidden>·</span>
            <Link to="/login">로그인</Link>
            <span aria-hidden>·</span>
            <Link to="/settings">설정</Link>
          </NavLinks>
        </DocFooter>
      </Doc>
      </Page>
      <SiteFooter $compact />
    </Shell>
  );
}

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(
      900px 480px at 50% -10%,
      ${({ theme }) => theme.colors.accentGlow} 0%,
      transparent 55%
    ),
    ${({ theme }) => theme.colors.background};
`;

const Page = styled.main`
  flex: 1;
  padding: 40px 20px 40px;
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  max-width: 760px;
  margin: 0 auto 28px;
  color: inherit;
`;

const BrandMark = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-size: 28px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: 1;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const BrandTitle = styled.strong`
  display: block;
  font-size: 20px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

const BrandSubtitle = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Doc = styled.article`
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 36px 40px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.xl};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  animation: slideUp 0.4s ease both;

  @media (max-width: 640px) {
    padding: 28px 20px;
  }
`;

const DocHeader = styled.header`
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  h1 {
    font-size: 28px;
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    letter-spacing: -0.02em;
    line-height: 1.2;
  }
`;

const Meta = styled.p`
  margin-top: 10px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Lead = styled.p`
  margin-top: 16px;
  font-size: 15px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Section = styled.section`
  margin-bottom: 36px;
  scroll-margin-top: 24px;
  ul { list-style: disc; }
  ol { list-style: decimal; }

  h2 {
    margin-bottom: 10px;
    font-size: 17px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  p {
    font-size: 15px;
    line-height: 1.85;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  ul,
  ol {
    margin: 10px 0 0;
    padding-left: 1.2em;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  li {
    font-size: 15px;
    line-height: 1.8;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`;

const Form = styled.form`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FormHint = styled.p`
  font-size: 12px !important;
  line-height: 1.6 !important;
  color: ${({ theme }) => theme.colors.textMuted} !important;
`;

const MailButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 50px;
  padding: 0 24px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  font-size: 15px;
  font-weight: 600;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
  }
`;

const ResultBox = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ResultCode = styled.code`
  display: block;
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primarySoft};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-align: center;
  color: ${({ theme }) => theme.colors.primaryHover};
  word-break: break-all;
`;

const ResultText = styled.p`
  font-size: 14px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};

  strong {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const GhostButton = styled.button`
  align-self: center;
  padding: 8px 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textMuted};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ContactBox = styled.div`
  margin-top: 12px;
  padding: 16px 18px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};

  p {
    font-size: 15px;
    line-height: 1.8;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  p + p {
    margin-top: 6px;
  }

  strong {
    display: inline-block;
    min-width: 52px;
    margin-right: 8px;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`;

const DocFooter = styled.footer`
  margin-top: 8px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  text-align: center;
`;

const NavLinks = styled.nav`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 13px;

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }

  span {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Contents = styled.nav`
  margin-bottom: 32px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  strong { display: block; margin-bottom: 10px; font-size: 14px; }
  ol { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 2px 20px; }
  a {
    display: flex;
    align-items: center;
    min-height: 40px;
    padding: 6px 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.textSecondary};
    &:hover { color: ${({ theme }) => theme.colors.primary}; text-decoration: underline; }
  }
  @media (max-width: 600px) {
    padding: 16px;
    ol { grid-template-columns: 1fr; }
    a { min-height: 44px; }
  }
`;
